// React
import { useEffect, useRef } from 'react';

/**
 * Fallback timer for CSS-animation-driven "auto-dismiss" UI (toast progress
 * bars, timeout pills, etc.). When the user has `prefers-reduced-motion`
 * set, the CSS animation never runs and its `onAnimationEnd` never fires,
 * so components relying on it would stick forever. This hook schedules a
 * `setTimeout` that fires `onComplete` after `duration` ms in that case.
 *
 * @returns `true` when reduced motion is active and the consumer should
 *   skip rendering the animated element entirely (the hook will still call
 *   `onComplete` at the scheduled time).
 */
export function useReducedMotionFallback(duration: number, onComplete?: () => void) {
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    if (!prefersReducedMotion) return;
    const timer = setTimeout(() => onCompleteRef.current?.(), duration);
    return () => clearTimeout(timer);
  }, [duration, prefersReducedMotion]);

  return prefersReducedMotion;
}
